/**
 * GroupedBarWidget — chart_type "grouped_bar".
 *
 * Adds segment tabs on top of the existing grouped-bar chart. The payload now
 * arrives in one of two shapes and both must render:
 *
 *   flat (existing)  `data` IS the chart input — { stages: [...], ... } — or
 *                    the `{ data: { stages } }` wrapper the renderer already
 *                    accepts. No tabs, unchanged behaviour.
 *   segmented (new)  `data` holds several complete chart inputs side by side,
 *                    keyed by segment: { all: {...}, "Med Precert": {...},
 *                    "Rx PA": {...} }.
 *
 * Everything below the tabs is the untouched ChartWidget → ChartFactory →
 * renderGroupedBar path: the active segment is handed over as if it were the
 * whole payload, so the renderer needs no knowledge of segments and the
 * backend-driven download items keep working exactly as before.
 */

import { useMemo, useState } from "react";

import ChartWidget from "./ChartWidget";
import SegmentTabs from "./SegmentTabs";
import type { WidgetDownloadOptions } from "./DownloadOptionsMenu";

type GroupedBarWidgetProps = {
  isLoading: boolean;
  title: string;
  description?: string | null;
  data: unknown;
  spec?: Record<string, unknown>;
  // Builder-saved default tab (charts/widgetConfig.ts). A key that is no
  // longer in the payload falls through to the first segment below.
  defaultTab?: string;
  widgetId?: string | number;
  dashboardName?: string;
  appliedFilters?: Record<string, unknown>;
  hasDownloadOptions?: boolean;
  downloadOptions?: WidgetDownloadOptions;
};

type SegmentEntry = { key: string; label: string; payload: unknown };

// Only the fields this file reads; the rest of the segment passes through to
// the renderer untouched.
type Comparison = {
  prior_value?: number | null;
  variance_seconds?: number | null;
  normalized_value?: number | null;
};

type SegmentPayload = {
  stages?: unknown[];
  avg_total_lifecycle_human?: string;
  complete_cycle_cases?: number | null;
  comparison?: Comparison | null;
};

/** The segment holding the combined figures; its tab is the only one that
 *  gets the PA Type Comparison group. */
const ALL_SEGMENT_KEY = "all";

/** Frontend-chosen: the backend sends no colour for a stage it doesn't send.
 *  Matches the magenta the old single-payload chart used for this group. */
const PA_TYPE_STAGE_COLOR = "#DB2777";

const isNumeric = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * A segment's CURRENT average, in the same normalized unit the bars use.
 *
 * The payload gives the current figure only as human text
 * ("7d 39m") — the numeric fields on `comparison` all describe the PRIOR
 * period. So it is recovered by scaling the prior normalized value by how
 * much the raw seconds moved:
 *
 *   current = prior_normalized × (prior_value + variance_seconds) / prior_value
 *
 * Deliberately ratio-based rather than `normalized_value + variance_hours`:
 * that shortcut silently assumes normalized_unit is hours, and would produce
 * a wrong-by-a-factor bar the day the backend normalizes to days. Verified
 * against all three segments — Med Precert yields 168.67h, which is exactly
 * the "7d 39m" the payload reports.
 */
const currentNormalizedValue = (comparison?: Comparison | null): number | null => {
  if (!comparison) return null;
  const { prior_value: prior, variance_seconds: variance, normalized_value: priorNormalized } = comparison;
  if (!isNumeric(prior) || !isNumeric(variance) || !isNumeric(priorNormalized)) return null;
  // A zero prior gives no ratio to scale by, and no way to place the bar.
  if (prior === 0) return null;
  const value = priorNormalized * ((prior + variance) / prior);
  return Number.isFinite(value) ? value : null;
};

/**
 * Builds the "PA Type Comparison" group shown on the All tab: one bar per PA
 * type, carrying that type's own overall average and prior comparison.
 *
 * The old single-payload chart received this as a real stage from the
 * backend. The segmented payload no longer sends it — the same numbers now
 * live on each sibling segment's own totals — so it is reassembled here.
 * Returns null when nothing can be built, leaving the chart exactly as the
 * backend sent it rather than rendering a group with empty bars.
 */
const buildTypeComparisonStage = (
  segments: SegmentEntry[],
  stageOrder: number,
): Record<string, unknown> | null => {
  const subStages = segments
    .filter((segment) => segment.key !== ALL_SEGMENT_KEY)
    .map((segment, index) => {
      const payload = segment.payload as SegmentPayload;
      const normalized = currentNormalizedValue(payload.comparison);
      if (normalized === null || !payload.avg_total_lifecycle_human) return null;
      return {
        key: segment.key,
        // The segment's own name, so a bar here and its tab read the same.
        label: segment.label,
        display_value: payload.avg_total_lifecycle_human,
        normalized_value: normalized,
        order: index + 1,
        cases_participated: payload.complete_cycle_cases ?? undefined,
        comparison: payload.comparison ?? undefined,
      };
    })
    .filter((sub): sub is NonNullable<typeof sub> => sub !== null);

  if (subStages.length === 0) return null;

  return {
    key: "pa_type_comparison",
    label: "PA Type Comparison",
    color: PA_TYPE_STAGE_COLOR,
    order: stageOrder,
    sub_stages: subStages,
  };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A complete grouped-bar input is recognised by its `stages` array. */
const hasStages = (value: unknown): boolean =>
  isPlainObject(value) && Array.isArray(value["stages"]);

/**
 * Tab text from the payload key. These keys are already display-ready
 * ("Med Precert", "Rx PA") apart from "all", so this only capitalises the
 * first letter of each word and leaves existing capitals alone — upper-casing
 * the whole word would turn "Rx PA" into "RX PA".
 */
const keyToLabel = (key: string): string =>
  key
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/**
 * Splits the payload into segments, or returns none for the flat shape.
 *
 * Order matters here. `raw.stages` is checked first so a flat payload never
 * reaches the segment scan, and `raw.data.stages` second because the renderer
 * has long accepted that wrapper — without this branch the scan would see a
 * key named "data" holding stages and render a single bogus "Data" tab.
 */
export const parseSegments = (raw: unknown): SegmentEntry[] => {
  if (!isPlainObject(raw)) return [];
  if (hasStages(raw)) return [];
  if (hasStages(raw["data"])) return [];

  return Object.entries(raw)
    .filter(([, value]) => hasStages(value))
    .map(([key, value]) => ({
      key,
      label: keyToLabel(key),
      payload: value,
    }));
};

export default function GroupedBarWidget({
  isLoading,
  title,
  description,
  data,
  spec,
  defaultTab,
  widgetId,
  dashboardName,
  appliedFilters,
  hasDownloadOptions,
  downloadOptions,
}: GroupedBarWidgetProps) {
  // Tracked by payload key rather than index so a filter change that reorders
  // or renames the segments can't leave the selection on a different one.
  // null resolves to the first segment below, so no effect is needed to seed
  // or re-validate it.
  const [activeKey, setActiveKey] = useState<string | null>(defaultTab ?? null);

  const segments = useMemo(() => parseSegments(data), [data]);

  // A key that is no longer present falls back to the first segment — the
  // same path as the initial render.
  const activeSegment =
    segments.find((segment) => segment.key === activeKey) ?? segments[0];

  // Flat payload: hand the whole thing through untouched. On the All tab the
  // PA Type Comparison group is appended, so the combined view still shows
  // how the types compare — the same group the pre-tabs chart had.
  const chartData = useMemo(() => {
    if (!activeSegment) return data;
    if (activeSegment.key !== ALL_SEGMENT_KEY) return activeSegment.payload;

    const payload = activeSegment.payload as SegmentPayload;
    const stages = Array.isArray(payload.stages) ? payload.stages : [];
    const typeStage = buildTypeComparisonStage(segments, stages.length + 1);
    if (!typeStage) return activeSegment.payload;

    // New object, so the query cache's copy is never mutated — appending in
    // place would compound the group on every re-render.
    return { ...payload, stages: [...stages, typeStage] };
  }, [activeSegment, segments, data]);

  return (
    <div className="grouped-bar-widget d-flex flex-column h-100">
      <SegmentTabs
        segments={segments}
        activeKey={activeSegment?.key}
        onSelect={setActiveKey}
        ariaLabel={title}
      />
      <div className="grouped-bar-widget__chart flex-fill">
        <ChartWidget
          isLoading={isLoading}
          title={title}
          description={description}
          data={chartData}
          spec={spec}
          chartType="grouped_bar"
          widgetId={widgetId}
          dashboardName={dashboardName}
          appliedFilters={appliedFilters}
          hasDownloadOptions={hasDownloadOptions}
          // Case Details is gated on has_download_options here, not on
          // download_options.case_data: the backend currently sends
          // case_data:false for this widget while still advertising
          // has_download_options:true, and the per-case export is wanted
          // regardless. Scoped to grouped_bar on purpose -- ChartWidget and
          // the ChartFactory menu are shared with bar/pie/line/map, which
          // still honour the case_data flag as sent.
          downloadOptions={
            hasDownloadOptions
              ? { ...downloadOptions, case_data: true }
              : downloadOptions
          }
        />
      </div>
    </div>
  );
}
